
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import { ClassHour, UploadFileResponse } from '@/api/typing';
import { UploadFileType } from '@/enums';
import UploadFileInput from '@/components/UploadFileInput.vue';

import { Api } from '@/util/api';
import {
  ClassHour as ApiClassHour,
  ClassHourSummaryComment,
  ClassHourSummaryRequest,
  GetClassHourSummaryResponse,
  VisitableUploadedFile,
} from '@orbitxedu/client-orbitxedu-typescript-axios';
import UploadFileInputV2 from '@/components/UploadFileInputV2.vue';

@Component({
  name: 'TeacherClassHourUploadSummaryFeedback',
  components: {
    UploadFileInput,
    UploadFileInputV2,
  },
})
export default class TeacherClassHourUploadSummaryFeedback extends Vue {
  classHour: ClassHour | ApiClassHour | null = null;

  @Prop({ type: Boolean, required: false, default: true })
  isModal?: boolean;

  loadingPreviousContent = true;

  formData: ClassHourSummaryRequest = {
    detailedContent: '',
    homeworkContent: '',
    homeworkImageFileIds: [],
    noteImageFileIds: [],
    videoFileIds: [],
    previewContent: '',
    studentSummaries: [] as ClassHourSummaryComment[],
  };

  uploadTypes = {
    note: UploadFileType.COURSE_SUMMARY_NOTE,
    homework: UploadFileType.COURSE_SUMMARY_HOMEWORK,
    video: UploadFileType.COURSE_SUMMARY_VIDEO,
  };

  get studentNames(): string[] {
    return this.classHour?.stus?.split(',') ?? [];
  }

  noteImages: UploadFileResponse[] = [];

  @Watch('noteImages')
  onNoteImagesChanged(...args: any[]) {
    const fileIds = this.noteImages.map((image) => image?.response?.file?.id).filter((id) => id != null);
    this.$set(this.formData, 'noteImageFileIds', fileIds);
  }

  homeworkImages: UploadFileResponse[] = [];

  @Watch('homeworkImages')
  onHomeworkImagesChanged(...args: any[]) {
    const fileIds = this.homeworkImages.map((image) => image?.response?.file?.id).filter((id) => id != null);
    this.$set(this.formData, 'homeworkImageFileIds', fileIds);
  }

  videoFiles: UploadFileResponse[] = [];

  @Watch('videoFiles')
  onVideoFilesChanged(...args: any[]) {
    const fileIds = this.videoFiles.map((video) => video?.response?.file?.id).filter((id) => id != null);
    this.$set(this.formData, 'videoFileIds', fileIds);
  }

  formRules = {
    detailedContent: [{ required: true, trigger: 'blur' }],
    previewContent: [{ required: true, trigger: 'blur' }],
    studentSummaries: [{ required: true, type: 'array', trigger: 'change' }],
    'studentSummaries.comments': [{ required: true, trigger: 'blur', message: 'Comments need' }],
  };

  uploadDoneVisible = false;

  openUploadDoneVisible() {
    this.uploadDoneVisible = true;
  }

  closeWindow() {
    window.close();
  }

  backToClass() {
    this.$router.push(`/classhour?classId=${this.classHour?.classid}`);
  }

  get isEdit() {
    return Boolean(this.classHour?.summaryId);
  }

  submiting = false;

  noteImagesUploading = false;
  homeworkImagesUploading = false;
  videoFilesUploading = false;

  get isAnyFileUploading(): boolean {
    return this.noteImagesUploading || this.homeworkImagesUploading || this.videoFilesUploading;
  }

  onNoteImagesUploadingChange(uploading: boolean) {
    this.noteImagesUploading = uploading;
  }

  onHomeworkImagesUploadingChange(uploading: boolean) {
    this.homeworkImagesUploading = uploading;
  }

  onVideoFilesUploadingChange(uploading: boolean) {
    this.videoFilesUploading = uploading;
  }

  @Emit('submit-feedback')
  async handleSubmit(): Promise<void> {
    if (this.isAnyFileUploading) {
      const result = await this.$confirm('Some files still uploading', 'Submit now?', {
        confirmButtonText: 'Save directly',
        cancelButtonText: 'Save later',
      }).catch(() => null);
      if (!result) {
        return;
      }
    }

    this.submiting = true;
    localStorage.removeItem(this.draftStorageKey);
    const valid = await (this.$refs.feedbackForm as any).validate();
    if (!valid) {
      this.submiting = false;
      return console.debug('validate failed');
    }
    try {
      if (this.classHour?.summaryId) {
        await Api.current.classHourApi.updateSummaryUsingPUT(
          this.classHour.id!,
          this.classHour.summaryId,
          this.formData,
        );
      } else {
        const response = await Api.current.classHourApi.createSummaryUsingPOST(this.classHour?.id!, this.formData);
        this.classHour!.summaryId = response.data.id;
      }

      this.openUploadDoneVisible();
    } finally {
      this.submiting = false;
    }
  }

  get draftStorageKey(): string {
    return 'draft-class-hour-report-' + this.classHour!.id;
  }

  @Emit('submit-feedback')
  async saveDraft(): Promise<void> {
    const valid = await (this.$refs.feedbackForm as any).validate();
    if (!valid) {
      return console.debug('validate failed');
    }
    const draftData: GetClassHourSummaryResponse = {
      classHourId: this.classHour!.id!,
      detailedContent: this.formData.detailedContent,
      homeworkContent: this.formData.homeworkContent,
      homeworkImageFiles: this.homeworkImages.map((image) => image?.response?.file).filter((file) => !!file) as any,
      id: -1,
      noteImageFiles: this.noteImages.map((image) => image?.response?.file).filter((file) => !!file) as any,
      previewContent: this.formData.previewContent,
      studentSummaries: this.formData.studentSummaries,
      videoFiles: this.videoFiles.map((image) => image?.response?.file).filter((file) => !!file) as any,
    };
    localStorage.setItem(this.draftStorageKey, JSON.stringify(draftData));
    this.$message.success('Saved draft to local browser, remember to submit it later');
  }

  get triggerButtonText() {
    return this.isEdit ? 'Edit Reports' : 'Upload Reports';
  }

  mapToFile(file: VisitableUploadedFile): UploadFileResponse {
    return {
      url: file.url,
      // id: image.id,
      name: file.fileName,
      response: {
        file,
      },
    };
  }

  @Watch('triggerButtonText')
  async updateTabTitle() {
    document.title = `${this.triggerButtonText} | ${this.classHour?.course} | ${this.classHour?.time}`;
  }

  async initClassHourData() {
    if (!this.classHour?.summaryId) {
      this.loadingPreviousContent = false;
      const draftData = localStorage.getItem(this.draftStorageKey);
      if (draftData) {
        this.loadFormData(JSON.parse(draftData));
      }
      return;
    }

    this.loadingPreviousContent = true;
    const { data: summary } = await Api.current.classHourApi
      .getSummaryUsingGET(this.classHour.id!, this.classHour.summaryId)
      .finally(() => {
        this.loadingPreviousContent = false;
      });

    this.loadFormData(summary);
  }

  private loadFormData(summary: GetClassHourSummaryResponse) {
    this.$set(this.formData, 'detailedContent', summary.detailedContent);
    this.$set(this.formData, 'previewContent', summary.previewContent);
    this.$set(this.formData, 'homeworkContent', summary.homeworkContent);
    this.$set(this, 'noteImages', summary.noteImageFiles?.map(this.mapToFile) ?? []);
    this.$set(this, 'homeworkImages', summary.homeworkImageFiles?.map(this.mapToFile) ?? []);
    this.$set(this, 'videoFiles', summary.videoFiles?.map(this.mapToFile) ?? []);

    const studentComments = new Map(summary.studentSummaries.map((comment) => [comment.studentId, comment.comments]));
    const studentSummaries = this.formData.studentSummaries.map((studentSummary) => ({
      ...studentSummary,
      comments: studentComments.get(studentSummary.studentId),
    }));
    this.$set(this.formData, 'studentSummaries', studentSummaries);
  }

  initStudentSummaries() {
    this.formData.studentSummaries = this.classHour!.sids!.split(',')
      .map(Number)
      .map((id, index) => ({
        studentId: Number(id),
        comments: '',
      }));
  }

  beforeWindowUnload = (event: BeforeUnloadEvent) => {
    if (this.isAnyFileUploading) {
      const message = 'Are you sure to close? Something you updated but did not save yet ..';
      event.returnValue = message;
      return message; // Gecko, WebKit, Chrome 34+, old Safari
    }
  };

  mounted() {
    const classHourId = this.$route.params?.classHourId!;
    Api.current.classHourApi.getClassHourByIdUsingGET(Number(classHourId)).then(({ data }) => {
      this.classHour = data;
      this.initClassHourData();
      this.initStudentSummaries();
    });
    window.addEventListener('beforeunload', this.beforeWindowUnload);
  }
}
